/**
 * useSettingsToast Hook
 * 
 * Shows App Bridge toast notifications when settings are toggled/changed.
 * Uses Shopify's App Bridge `shopify.toast.show()` API for consistent UX.
 * 
 * @see https://shopify.dev/docs/api/app-bridge-library/apis/toast
 */

import { useCallback, useRef } from 'react';

interface SettingsToastOptions {
  /** Toast duration in milliseconds (default: 3000ms) */
  duration?: number;
}

type SettingLabel = {
  [key: string]: string;
};

/**
 * Human-readable labels for settings fields
 */
const SETTING_LABELS: SettingLabel = {
  // Delivery Preferences
  timezone: 'Timezone',
  weekly_summary_enabled: 'Weekly summary',
  // Weekly Digest
  weekly_digest_day: 'Digest day',
  weekly_digest_time: 'Digest time',
  weekly_digest_sections: 'Digest sections',
  // Other Delivery Settings
  email_address: 'Email address',
  quiet_hours_enabled: 'Quiet hours',
  quiet_hours_start: 'Quiet hours start',
  quiet_hours_end: 'Quiet hours end',
  quiet_hours_timezone: 'Quiet hours timezone',
  browser_notifications_enabled: 'Push notifications',
  // Velocity Configuration
  velocity_lookback_days: 'Lookback period',
  minimum_data_days: 'Minimum data days',
  // Notification Rules
  alert_frequency: 'Alert frequency',
  digest_time: 'Daily digest time',
  alert_level_filters: 'Alert level filters',
  notification_grouping: 'Notification grouping',
  // Data Export Settings
  export_format: 'Export format',
  auto_export_enabled: 'Automatic exports',
  auto_export_frequency: 'Export frequency',
  data_retention_days: 'Data retention',
};

/**
 * Format a setting value for display in toast
 */
function formatValue(value: unknown): string {
  if (typeof value === 'boolean') {
    return value ? 'enabled' : 'disabled';
  }
  if (Array.isArray(value)) {
    return value.length === 0 ? 'none' : `${value.length} selected`;
  }
  if (typeof value === 'number') {
    // Handle day of week
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    if (value >= 0 && value <= 6 && Number.isInteger(value)) {
      return days[value];
    }
    return String(value);
  }
  if (typeof value === 'string') {
    return value;
  }
  return String(value);
}

/**
 * Hook for showing toasts when settings are changed
 * 
 * @example
 * ```tsx
 * const { showSettingChangedToast } = useSettingsToast();
 * 
 * // When a setting changes
 * const handleToggle = (enabled: boolean) => {
 *   updateField('browser_notifications_enabled', enabled);
 *   showSettingChangedToast('browser_notifications_enabled', enabled);
 * };
 * ```
 */
export function useSettingsToast(options: SettingsToastOptions = {}) {
  const { duration = 3000 } = options;
  
  // Debounce timer reference to prevent rapid-fire toasts
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastToastRef = useRef<{ field: string; time: number } | null>(null);

  /**
   * Show toast notification for a setting change
   * Uses App Bridge toast API when available, falls back silently otherwise
   */
  const showSettingChangedToast = useCallback(
    (fieldName: string, newValue: unknown) => {
      // Prevent duplicate toasts for same field within 500ms
      const now = Date.now();
      if (
        lastToastRef.current?.field === fieldName &&
        now - lastToastRef.current.time < 500
      ) {
        return;
      }

      // Clear any pending debounce
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Small debounce to batch rapid changes
      debounceTimerRef.current = setTimeout(() => {
        const label = SETTING_LABELS[fieldName] || fieldName;
        const formattedValue = formatValue(newValue);
        
        // Determine toast message based on value type
        let message: string;
        if (typeof newValue === 'boolean') {
          message = `${label} ${formattedValue}`;
        } else {
          message = `${label} updated to ${formattedValue}`;
        }

        // Use App Bridge toast if available
        if (typeof window !== 'undefined' && window.shopify?.toast) {
          window.shopify.toast.show(message, { duration });
        }

        // Track last toast
        lastToastRef.current = { field: fieldName, time: Date.now() };
      }, 100);
    },
    [duration]
  );

  /**
   * Show a custom toast message
   */
  const showToast = useCallback(
    (message: string, isError = false) => {
      if (typeof window !== 'undefined' && window.shopify?.toast) {
        window.shopify.toast.show(message, { 
          duration, 
          isError 
        });
      }
    },
    [duration]
  );

  return {
    showSettingChangedToast,
    showToast,
  };
}
